import { useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { useNavigate } from 'react-router-dom'
import type { DocumentDto, FolderDto } from '../lib/types'

interface DocListProps {
  docs: DocumentDto[]
  folders: FolderDto[]
}

interface Row {
  uuid: string
  title: string
  folder: string
  updatedAt: string
  sizeBytes: number
}

export function DocList({ docs, folders }: DocListProps) {
  const navigate = useNavigate()

  const folderNameById = useMemo(() => {
    const map = new Map<number, string>()
    const walk = (arr: FolderDto[]) => {
      for (const f of arr) {
        map.set(f.id, f.name)
        if (f.children) walk(f.children)
      }
    }
    walk(folders)
    return map
  }, [folders])

  const rowData = useMemo<Row[]>(() => docs.filter((d) => !d.archived).map((d) => ({
    uuid: d.uuid,
    title: d.title,
    folder: d.folderId != null ? (folderNameById.get(d.folderId) ?? '—') : '(root)',
    updatedAt: d.updatedAt,
    // backend 가 sizeBytes 를 노출하면 그대로, 없으면 wordCount * 5 추정 (구버전 호환)
    sizeBytes: typeof d.sizeBytes === 'number' ? d.sizeBytes : d.wordCount * 5,
  })), [docs, folderNameById])

  const columnDefs = useMemo<ColDef<Row>[]>(() => [
    { field: 'title', headerName: 'Title', flex: 2, minWidth: 200, filter: true, sortable: true },
    { field: 'folder', headerName: 'Folder', flex: 1, minWidth: 120, filter: true, sortable: true },
    { field: 'updatedAt', headerName: 'Updated', minWidth: 160, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString() : '' },
    { field: 'sizeBytes', headerName: 'Size', minWidth: 100, sortable: true,
      valueFormatter: (p) => formatBytes(p.value as number) },
  ], [])

  return (
    <div id="doc-list-wrap" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '12px 16px 16px' }}>
      <div id="doc-list" className="ag-theme-quartz" style={{ flex: 1, minHeight: 0 }}>
        <AgGridReact<Row>
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={(params) => params.data.uuid}
          pagination
          paginationPageSize={50}
          paginationPageSizeSelector={[20, 50, 100]}
          onRowClicked={(e) => { if (e.data) navigate(`/d/${e.data.uuid}`) }}
          rowStyle={{ cursor: 'pointer' }}
        />
      </div>
    </div>
  )
}

function formatBytes(n: number): string {
  if (!n || n <= 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}
